import type { GetAllInput } from "@types_def/common/filter.types";
import type { QueryType } from "@types_def/common/query.types";

import {
  FindAllClientsDocument,
  FindAllDeliveryDocument,
  FindAllDeliveryPaymentsDocument,
  FindAllDriversDocument,
} from "@/libs/generated/hooks";
import { graphqlFetcher } from "@/libs/graphql-fetcher";

async function getAllFilteredDeep<T extends Record<string, unknown>, K extends keyof T>(
  getAllInput: GetAllInput<T, K>,
  resource: string,
  _graphQlFieldsObject: QueryType<T>,
  _customizedInputName?: string
) {
  const normalizedInput: GetAllInput<T, K> = {
    ...getAllInput,
    sort: getAllInput.sort
      ? {
          ...getAllInput.sort,
          field: getAllInput.sort.field === "publicId" ? ("id" as K) : getAllInput.sort.field,
        }
      : undefined,
  };

  try {
    switch (resource) {
      case "Delivery": {
        const fetcher = graphqlFetcher<
          { findAllDelivery: { records: T[]; total: number } },
          { GetAllInput: typeof normalizedInput }
        >(FindAllDeliveryDocument, { GetAllInput: normalizedInput });
        const response = await fetcher();

        return { success: true, message: "Ok", data: response.findAllDelivery };
      }
      case "Drivers": {
        const fetcher = graphqlFetcher<
          { findAllDrivers: { records: T[]; total: number } },
          { input: typeof normalizedInput }
        >(FindAllDriversDocument, { input: normalizedInput });
        const response = await fetcher();
        return { success: true, message: "Ok", data: response.findAllDrivers };
      }
      case "Clients": {
        const fetcher = graphqlFetcher<
          { findAllClients: { records: T[]; total: number } },
          { GetAllInput: typeof normalizedInput }
        >(FindAllClientsDocument, { GetAllInput: normalizedInput });
        const response = await fetcher();
        return { success: true, message: "Ok", data: response.findAllClients };
      }
      case "DeliveryPayments": {
        const fetcher = graphqlFetcher<
          { findAllDeliveryPayments: { records: T[]; total: number } },
          { GetAllInput: typeof normalizedInput }
        >(FindAllDeliveryPaymentsDocument, { GetAllInput: normalizedInput });
        const response = await fetcher();
        return { success: true, message: "Ok", data: response.findAllDeliveryPayments };
      }
      default:
        throw new Error(`Unsupported resource for generated list query: ${resource}`);
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Request failed";
    throw new Error(`Failed to fetch ${resource}: ${errorMessage}`);
  }
}

export default getAllFilteredDeep;
